import React from "react";
import { Trophy, Clock, Pizza } from "lucide-react";
import { UserStats } from "../types";
import { formatTime } from "../utils/formatTime";

interface RankingItemProps {
  pizzaiolo: UserStats;
  index: number;
}

export const RankingItem: React.FC<RankingItemProps> = ({
  pizzaiolo,
  index,
}) => {
  const isFirst = index === 0;
  const isSecond = index === 1;
  const isThird = index === 2;

  return (
    <div
      className={`relative rounded-lg transition-all duration-300 sm:rounded-xl ${
        isFirst
          ? "border border-yellow-400/40 bg-gradient-to-r from-yellow-500/30 to-yellow-600/20"
          : isSecond
            ? "border border-neutral-400/30 bg-gradient-to-r from-neutral-400/20 to-neutral-500/10 shadow-lg"
            : isThird
              ? "border border-amber-500/30 bg-gradient-to-r from-amber-600/20 to-amber-700/10 shadow-lg"
              : "border border-white/20 bg-white/10 backdrop-blur-sm"
      }`}
    >
      <div className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0 sm:px-6 sm:py-4">
        <div className="flex items-center space-x-3 sm:space-x-4">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full sm:h-12 sm:w-12 ${
              isFirst
                ? "bg-gradient-to-br from-yellow-400 to-yellow-600 shadow-lg"
                : isSecond
                  ? "bg-gradient-to-br from-neutral-300 to-neutral-500 shadow-lg"
                  : isThird
                    ? "bg-gradient-to-br from-amber-500 to-amber-700 shadow-lg"
                    : "bg-white/20"
            }`}
          >
            {index < 3 ? (
              <Trophy className="h-5 w-5 text-white sm:h-6 sm:w-6" />
            ) : (
              <span className="text-base font-black text-white sm:text-lg">
                #{index + 1}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className={`mb-0.5 truncate text-lg font-black sm:mb-1 sm:text-2xl ${
                isFirst ? "text-yellow-300" : "text-white"
              }`}
            >
              {pizzaiolo.nome} {pizzaiolo.sobrenome}
            </h3>
            <div className="flex flex-wrap items-center gap-2 sm:space-x-3">
              <div className="flex items-center space-x-1.5 rounded-full bg-white/10 px-2 py-0.5 sm:py-1">
                <Pizza className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="text-sm font-semibold sm:text-base">
                  {pizzaiolo.producao} pizzas
                </span>
              </div>
              {isFirst && (
                <div className="flex items-center space-x-1 rounded-full border border-yellow-400/30 bg-yellow-500/20 px-2 py-0.5 sm:py-1">
                  <Trophy className="h-2.5 w-2.5 text-yellow-400 sm:h-3 sm:w-3" />
                  <span className="text-[10px] font-medium text-yellow-300 sm:text-xs">
                    LÍDER
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-white/10 pt-2 sm:border-0 sm:pt-0 sm:text-right">
          <span className="text-xs opacity-75 sm:hidden">tempo médio</span>
          <div className="flex items-center space-x-1.5 sm:mb-1 sm:block sm:space-x-2">
            <Clock className="h-4 w-4 opacity-75 sm:h-5 sm:w-5" />
            <span
              className={`text-xl font-black sm:text-3xl ${
                isFirst ? "text-yellow-300" : "text-white"
              }`}
            >
              {formatTime(pizzaiolo.tempoMedio)}
            </span>
          </div>
          <p className="hidden text-sm font-medium opacity-75 sm:block">
            tempo médio por pizza
          </p>
        </div>
      </div>
    </div>
  );
};
